import { useEffect, useState } from "react";
import { LogoutButton } from "@/components/LogoutButton";
import { AppFrame } from "@/components/AppFrame";
import { AdminTopicsSection } from "@/admin/AdminTopicsSection";
import { AdminTimerSection } from "@/admin/AdminTimerSection";
import { AdminRequirementsSection } from "@/admin/AdminRequirementsSection";
import { AdminEvalStatusSection } from "@/admin/AdminEvalStatusSection";
import { AdminAuditSection } from "@/admin/AdminAuditSection";
import { AdminArchiveSection } from "@/admin/AdminArchiveSection";
import {
  AdminFallbackBanner,
  AdminFallbackSection,
} from "@/admin/AdminFallbackSection";
import { AdminCodesSection } from "@/workshop/AdminCodesSection";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { AdminZeitleiste } from "@/admin/AdminZeitleiste";

type Tab =
  | "topics"
  | "timer"
  | "requirements"
  | "eval"
  | "audit"
  | "archive"
  | "fallback"
  | "codes";

const TABS: { id: Tab; label: string }[] = [
  { id: "topics", label: "Themen" },
  { id: "timer", label: "Timer" },
  { id: "requirements", label: "Anforderungen" },
  { id: "eval", label: "KI-Bewertung" },
  { id: "audit", label: "Verlauf" },
  { id: "archive", label: "Archiv" },
];

/**
 * Reiter, die es in einer Solo-Team-Sitzung nicht gibt: Ohne Voting werden
 * keine Themen gesammelt, und bewertet wird auch nichts — die Sitzung dauert
 * 25 Minuten. Sie standen trotzdem da, mit leeren Masken dahinter, und
 * kosteten den Moderator vor Publikum Suchzeit.
 */
const SOLO_HIDDEN_TABS: Tab[] = ["topics", "eval"];

export function AdminPage() {
  // Der Notfall-Modus gehört zur AR-Sitzung (ein Teilnehmer = ein Team).
  // Im Vertriebs-Offsite gibt es ihn nicht.
  const { soloTeams, codeLogin, stationsAblauf } = useOffsiteConfig();
  const tabs = soloTeams
    ? [
        ...TABS.filter((t) => !SOLO_HIDDEN_TABS.includes(t.id)),
        { id: "fallback" as Tab, label: "Notfall" },
      ]
    : TABS;
  // Einmal-Codes gibt es nur bei Code-Login (Strategietag) — ohne codeLogin
  // meldet sich niemand mit einem Code an, der Reiter wäre eine leere Maske.
  const allTabs = codeLogin
    ? [...tabs, { id: "codes" as Tab, label: "Codes" }]
    : tabs;
  const [tab, setTab] = useState<Tab>(soloTeams ? "timer" : "topics");

  useEffect(() => {
    document.documentElement.classList.add("skin-light");
    document.documentElement.classList.remove("skin-presenter");
  }, []);

  return (
    <AppFrame skin="light" hideHeader hideFooter>
      <main className="admin-shell">
      <div className="admin-canvas">
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div>
            <div className="deck-eyebrow" style={{ marginBottom: 8 }}>
              SVO Offsite · Administration
            </div>
            <h1
              className="deck-title"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", margin: 0 }}
            >
              Backend
            </h1>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <a href="/present" className="btn btn-ghost" style={{ textDecoration: "none" }}>
              ← Präsentation
            </a>
            <LogoutButton />
          </div>
        </header>

        {soloTeams && <AdminFallbackBanner />}

        {/* Kopf des Pults: Zeitleiste mit Soll/Ist und Gruppen-Steuerung —
            nur der Stations-Ablauf führt die servergeführten Uhren. */}
        {stationsAblauf && <AdminZeitleiste />}

        <nav role="tablist" className="admin-tablist">
          {allTabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              className={`admin-tab${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <section className="admin-card">
          {tab === "topics" && <AdminTopicsSection />}
          {tab === "timer" && <AdminTimerSection />}
          {tab === "requirements" && <AdminRequirementsSection />}
          {tab === "eval" && <AdminEvalStatusSection />}
          {tab === "audit" && <AdminAuditSection />}
          {tab === "archive" && <AdminArchiveSection />}
          {tab === "fallback" && soloTeams && <AdminFallbackSection />}
          {tab === "codes" && codeLogin && <AdminCodesSection />}
        </section>
      </div>
    </main>
    </AppFrame>
  );
}
